import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import { supabase } from "../../supabaseClient";
import { useViewerStudent } from "../../auth/ViewAsContext";

/**
 * Messages — everything said between one student and staff, in one timeline.
 *
 * Two channels feed it, and keeping them apart was the problem:
 *
 *   student_message     the general-question channel ("can I come to office
 *                       hours next week?")
 *   mentor_review_log   the review thread — an approval with its note, a
 *                       send-back with its comment, and the student's reply
 *
 * A student who answers an approval writes into the SECOND one, as a
 * `student_message` action on the review log. Nothing rendered that as a
 * conversation, so a reply sat between a mentor's comment and their next
 * approval, invisible. That is the whole reason this page merges rather than
 * showing one channel.
 *
 * Read-only. Replying stays where it already is — the questions panel for a
 * question, the review screen for a submission.
 */

type Side = "student" | "staff";

interface Entry {
  id: string;
  at: string;
  side: Side;
  who: string;
  body: string;
  /** Short badge: what kind of thing this was. */
  label: string;
  /** Review-thread entries carry more weight than a passing question. */
  fromReview: boolean;
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function ViewAsMessagesPage() {
  const { studentId, studentName, loading: vasLoading } = useViewerStudent();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError(null);

    const { data: proj } = await supabase
      .from("project")
      .select("project_id")
      .eq("student_id", studentId)
      .eq("status", "active")
      .maybeSingle();

    const [msgRes, logRes] = await Promise.all([
      supabase
        .from("student_message")
        .select("id, direction, routed_to, body, created_at, created_by")
        .eq("student_id", studentId),
      proj?.project_id
        ? supabase
            .from("mentor_review_log")
            .select("log_id, action, note, acted_at, reviewer_id, step_number")
            .eq("project_id", proj.project_id)
        : Promise.resolve({ data: [], error: null }),
    ]);

    if (msgRes.error) {
      setError(msgRes.error.message);
      setLoading(false);
      return;
    }

    const msgs = (msgRes.data ?? []) as {
      id: string; direction: string; routed_to: string;
      body: string; created_at: string; created_by: string | null;
    }[];
    const logs = ((logRes as { data?: unknown }).data ?? []) as {
      log_id: string; action: string; note: string | null;
      acted_at: string; reviewer_id: string | null; step_number: number | null;
    }[];

    /* Names for everyone who wrote anything, in one round trip. */
    const ids = Array.from(new Set([
      ...msgs.map((m) => m.created_by),
      ...logs.map((l) => l.reviewer_id),
    ].filter(Boolean) as string[]));
    const nameOf: Record<string, string> = {};
    if (ids.length > 0) {
      const { data: us } = await supabase
        .from("users")
        .select("user_id, first_name, last_name")
        .in("user_id", ids);
      for (const u of us ?? []) {
        nameOf[u.user_id] =
          `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || "Ardent staff";
      }
    }

    const out: Entry[] = [];

    for (const m of msgs) {
      const fromStudent = m.direction === "student_to_staff";
      out.push({
        id: `msg-${m.id}`,
        at: m.created_at,
        side: fromStudent ? "student" : "staff",
        who: fromStudent
          ? (studentName ?? "Student")
          : (m.created_by && nameOf[m.created_by]) || "Ardent staff",
        body: m.body,
        label: fromStudent
          ? `question · to ${m.routed_to === "office" ? "the office" : "mentor"}`
          : "reply",
        fromReview: false,
      });
    }

    for (const l of logs) {
      const note = (l.note ?? "").trim();
      /* 'schedule' carries no note — a calendar action, not something said. */
      if (!note) continue;
      const step = l.step_number != null ? `Step ${l.step_number} · ` : "";
      const isStudent = l.action === "student_message";
      const label =
        l.action === "approve" ? `${step}approved`
        : l.action === "comment" ? `${step}sent back`
        : isStudent ? "reply on review"
        : `${step}message`;
      out.push({
        id: `log-${l.log_id}`,
        at: l.acted_at,
        side: isStudent ? "student" : "staff",
        who: isStudent
          ? (studentName ?? "Student")
          : (l.reviewer_id && nameOf[l.reviewer_id]) || "Ardent staff",
        body: note,
        label,
        fromReview: true,
      });
    }

    out.sort((a, b) => a.at.localeCompare(b.at));
    setEntries(out);
    setLoading(false);
  }, [studentId, studentName]);

  useEffect(() => {
    if (!vasLoading) load();
  }, [vasLoading, load]);

  if (vasLoading || loading) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 780 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
        Messages
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Everything said between {studentName ?? "this student"} and Ardent
        staff — general questions and the review thread (approvals, send-backs
        and their replies) in one timeline. To reply, use the questions panel
        on your dashboard or the review screen for that step.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing said with {studentName ?? "this student"} yet.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {entries.map((e) => {
            const fromStudent = e.side === "student";
            return (
              <Box
                key={e.id}
                sx={{
                  display: "flex",
                  justifyContent: fromStudent ? "flex-start" : "flex-end",
                }}
              >
                <Box
                  sx={{
                    maxWidth: "82%",
                    px: 1.75,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: fromStudent ? "action.hover" : "primary.main",
                    color: fromStudent ? "text.primary" : "primary.contrastText",
                    /* Review-thread entries are the decisions and the answers
                       to them; a passing question is not. */
                    border: e.fromReview ? 2 : 0,
                    borderColor: "secondary.main",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ display: "block", opacity: 0.85, mb: 0.25 }}
                  >
                    {e.who} · {fmt(e.at)}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={e.label}
                      sx={{
                        ml: 0.75,
                        height: 18,
                        color: "inherit",
                        borderColor: "currentColor",
                        opacity: 0.85,
                      }}
                    />
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}
                  >
                    {e.body}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
